import React from "react";
import { Box, Typography, Card } from "@mui/material";
import { badgesData } from "@/constants/badges";

interface BadgesProps {
    contributionCount: number | string;
}


const Badges: React.FC<BadgesProps> = ({ contributionCount }) => {

    return (
        <Box sx={{ mt: 3, mb: 1 }}>
            <Typography
                sx={{
                    fontSize: { xs: "4.37vw", sm: 16 },
                    fontWeight: 700,
                    color: "#313131",
                    mb: 2,
                }}
            >
                Badges
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    overflowX: "auto",
                    pb: 1,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {badgesData.map((badge, index) => {
                    const count = Number(contributionCount) || 0;
                    const isUnlocked = count >= badge.target;
                    const nextBadgeIndex = badgesData.findIndex(b => count < b.target);
                    const isNext = index === nextBadgeIndex;

                    let progress = 0;
                    let strokeColor = "#7747FB"; // Default purple for unlocked

                    if (isUnlocked) {
                        progress = 100;
                    } else if (isNext) {
                        progress = Math.min((count / badge.target) * 100, 100);
                        strokeColor = "#626262"; // Dark grey for "next" badge progress
                    } else {
                        progress = 0;
                    }

                    return (
                        <Card
                            key={badge.id}
                            sx={{
                                minWidth: 130,
                                flexShrink: 0,
                                borderRadius: "12px",
                                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
                                border: "1px solid #F2F2F2",
                                textAlign: "center",
                                p: 1.5,
                                bgcolor: "white",
                                filter: isUnlocked ? "none" : "grayscale(1)",
                                opacity: isUnlocked ? 1 : 0.6,
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    display: "inline-flex",
                                    mb: 1,
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                {/* SVG for Circular Progress */}
                                <Box
                                    component="svg"
                                    viewBox="0 0 60 60"
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        transform: "rotate(-90deg)",
                                    }}
                                >
                                    {/* Background Circle */}
                                    <circle
                                        cx="30"
                                        cy="30"
                                        r="26"
                                        fill="none"
                                        stroke="#F2F2F2"
                                        strokeWidth="3"
                                    />
                                    {/* Progress Circle */}
                                    <circle
                                        cx="30"
                                        cy="30"
                                        r="26"
                                        fill="none"
                                        stroke={strokeColor}
                                        strokeWidth="3"
                                        strokeDasharray={2 * Math.PI * 26}
                                        strokeDashoffset={2 * Math.PI * 26 * (1 - progress / 100)}
                                        strokeLinecap="round"
                                        style={{
                                            transition: "stroke-dashoffset 0.5s ease",
                                        }}
                                    />
                                </Box>
                                <Box
                                    component="img"
                                    src={badge.icon}
                                    sx={{
                                        position: "absolute",
                                        width: 40,
                                        height: 40,
                                        zIndex: 1,
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: { xs: "3.3vw", sm: "1.8vw", md: "1.2vw" },
                                    fontWeight: 700,
                                    color: isUnlocked ? "#313131" : "#9E9E9E",
                                    lineHeight: 1.2,
                                    mb: 0.5,
                                }}
                            >
                                {badge.name}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "2.8vw", sm: "1.68vw", md: "1.2vw" },
                                    color: "#9E9E9E",
                                    fontWeight: 500,
                                }}
                            >
                                {badge.description}
                            </Typography>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Badges;
